import React, { useState, useEffect } from "react";
import { API_BASE } from "../apiConfig";
import { useNavigate, useSearchParams } from "react-router-dom";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faEye, faEyeSlash } from "@fortawesome/free-solid-svg-icons";
import { acceptTermsOfUse } from "../utils/api";
import { resolvePostLoginPath } from "../utils/postLoginRedirect";
import "./Login.css";

const Login = ({ onLogin }) => {
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [showPassword, setShowPassword] = useState(false);
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);
  const [companies, setCompanies] = useState([]);
  const [selectedCompanyId, setSelectedCompanyId] = useState("");
  const [termsRequired, setTermsRequired] = useState(false);
  const [termsChecked, setTermsChecked] = useState(false);
  const [termsSaving, setTermsSaving] = useState(false);
  const [termsError, setTermsError] = useState("");
  const [pendingUser, setPendingUser] = useState(null);
  const [pendingTermsVersion, setPendingTermsVersion] = useState("");
  const [showForgotPassword, setShowForgotPassword] = useState(false);
  const [forgotPasswordEmail, setForgotPasswordEmail] = useState("");
  const [forgotPasswordLoading, setForgotPasswordLoading] = useState(false);
  const [forgotPasswordError, setForgotPasswordError] = useState("");
  const [forgotPasswordSuccess, setForgotPasswordSuccess] = useState(false);
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();

  // Check if this is a first-time setup flow
  const isSetup = searchParams.get("setup") === "true";
  const mobileOnly = searchParams.get("mobileOnly") === "1";
  const returnTo = searchParams.get("returnTo") || "";

  useEffect(() => {
    setCompanies([]);
    setSelectedCompanyId("");
  }, [email, password]);
  useEffect(() => {
    if (mobileOnly) {
      setError("Caregivers and nurses must use the mobile app to sign in.");
    }
  }, [mobileOnly]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (companies.length > 0 && !selectedCompanyId) {
      setError("Please select a company to continue.");
      return;
    }
    setLoading(true);
    setError("");

    try {
      const payload = {
        email,
        password,
        client: "web",
        ...(selectedCompanyId ? { companyId: selectedCompanyId } : {}),
      };
      const controller = new AbortController();
      const timeoutId = window.setTimeout(() => controller.abort(), 25000);
      let response;
      try {
        response = await fetch(`${API_BASE}/api/auth/login`, {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
          },
          body: JSON.stringify(payload),
          signal: controller.signal,
        });
      } finally {
        window.clearTimeout(timeoutId);
      }

      const rawBody = await response.text();
      let data = {};
      if (rawBody) {
        try {
          data = JSON.parse(rawBody);
        } catch {
          data = { message: rawBody.trim() || "Login failed" };
        }
      }

      if (response.ok) {
        const accountType = String(data.user?.accountType || "").toLowerCase();
        if (accountType === "caregiver" || accountType === "nurse") {
          localStorage.removeItem("token");
          localStorage.removeItem("refreshToken");
          localStorage.removeItem("user");
          setError("Caregivers and nurses must use the mobile app to sign in.");
          return;
        }
        console.log("✅ [LOGIN] Successful login:", {
          user: data.user.name,
          email: data.user.email,
          mustChangePassword: data.user.mustChangePassword,
          hasToken: !!data.token,
        });

        // Successful login
        localStorage.setItem("token", data.token);
        if (data.refreshToken) {
          localStorage.setItem("refreshToken", data.refreshToken);
          // Soft expiry guard (localStorage has no native TTL).
          // Backend refresh token is configured for long-lived sessions.
          const ninetyDaysMs = 90 * 24 * 60 * 60 * 1000;
          localStorage.setItem(
            "refreshTokenExpiresAt",
            String(Date.now() + ninetyDaysMs),
          );
        }
        localStorage.setItem("user", JSON.stringify(data.user));

        const mustAcceptTerms =
          data.mustAcceptTerms || data.user?.mustAcceptTerms;
        const termsVersion = data.termsVersion || data.user?.termsVersion || "";

        // Check if user needs to setup account (change temp password)
        if (data.user.mustChangePassword) {
          console.log(
            "🔄 [LOGIN] Redirecting to /setup-account (must change password)",
          );
          if (mustAcceptTerms) {
            localStorage.setItem("termsPendingVersion", termsVersion);
          } else {
            localStorage.removeItem("termsPendingVersion");
          }
          navigate("/setup-account");
          if (onLogin) onLogin(data.user);
          return;
        }

        if (mustAcceptTerms) {
          localStorage.setItem("termsPendingVersion", termsVersion);
          setPendingUser(data.user);
          setPendingTermsVersion(termsVersion);
          setTermsChecked(false);
          setTermsError("");
          setTermsRequired(true);
          return;
        } else {
          localStorage.removeItem("termsPendingVersion");
          const postLoginPath = resolvePostLoginPath(data.user, returnTo);
          console.log(`🔄 [LOGIN] Redirecting to ${postLoginPath}`);
          navigate(postLoginPath);
          if (onLogin) onLogin(data.user);
        }
      } else if (response.status === 409 && data.requiresCompanySelection) {
        setCompanies(data.companies || []);
        setSelectedCompanyId("");
        setError("Select your company to continue.");
      } else {
        console.error("❌ [LOGIN] Failed:", data);
        setError(data.message || "Login failed");
      }
    } catch (err) {
      console.error("Login error:", err);
      const isAbort =
        err?.name === "AbortError" ||
        String(err?.message || "").toLowerCase().includes("abort");
      const networkMsg = isAbort
        ? "Login timed out. The server may be unreachable — try again, or contact support if this continues."
        : err?.message && /failed to fetch|network|load/i.test(err.message)
          ? "Cannot reach the server. Check your connection or try again in a moment."
          : "Server error. Please try again later.";
      setError(networkMsg);
    } finally {
      setLoading(false);
    }
  };

  const handleForgotPasswordOpen = () => {
    setShowForgotPassword(true);
    setForgotPasswordEmail(email);
    setForgotPasswordError("");
    setForgotPasswordSuccess(false);
  };

  const handleForgotPasswordClose = () => {
    setShowForgotPassword(false);
    setForgotPasswordEmail("");
    setForgotPasswordError("");
    setForgotPasswordSuccess(false);
  };

  const handleForgotPasswordSubmit = async (e) => {
    e.preventDefault();
    const trimmed = (forgotPasswordEmail || "").trim();
    if (!trimmed) {
      setForgotPasswordError("Please enter your email address.");
      return;
    }
    if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(trimmed)) {
      setForgotPasswordError("Please enter a valid email address.");
      return;
    }
    setForgotPasswordError("");
    setForgotPasswordLoading(true);
    try {
      const res = await fetch(`${API_BASE}/api/auth/forgot-password`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ email: trimmed }),
      });
      const data = await res.json().catch(() => ({}));
      if (res.ok) {
        setForgotPasswordSuccess(true);
      } else {
        setForgotPasswordError(data.message || "Request failed. Please try again.");
      }
    } catch (err) {
      setForgotPasswordError(err.message || "Request failed. Please try again.");
    } finally {
      setForgotPasswordLoading(false);
    }
  };

  const handleAcceptTerms = async () => {
    if (!termsChecked) {
      setTermsError("You must accept the Terms of Use to continue.");
      return;
    }
    if (!pendingUser) {
      setTermsError("User session not found. Please sign in again.");
      return;
    }
    try {
      setTermsSaving(true);
      setTermsError("");
      const updated = await acceptTermsOfUse({
        termsVersion: pendingTermsVersion || null,
      });
      const mergedUser = { ...pendingUser, ...updated };
      localStorage.setItem("user", JSON.stringify(mergedUser));
      localStorage.removeItem("termsPendingVersion");
      setTermsRequired(false);
      navigate(resolvePostLoginPath(mergedUser, returnTo));
    } catch (err) {
      setTermsError(err.message || "Failed to record Terms acceptance.");
    } finally {
      setTermsSaving(false);
    }
  };

  return (
    <div className="login-container">
      <div className="login-box">
        <div className="login-logo">
          <img src="/new-logo.png" alt="Albert" />
        </div>
        <h2 className="login-title">
          {isSetup ? "Welcome to ALBERTai!" : "Welcome Back"}
        </h2>
        <p className="login-subtitle">
          {isSetup
            ? "Login with your temporary password to complete setup"
            : "Sign in to access your dashboard"}
        </p>

        {error && <div className="login-error">{error}</div>}

        <form onSubmit={handleSubmit} className="login-form">
          <div className="form-group">
            <label htmlFor="email">Email Address</label>
            <input
              type="email"
              id="email"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              placeholder="Enter your email"
              required
            />
          </div>

          <div className="form-group">
            <label htmlFor="password">Password</label>
            <div className="password-input-wrapper">
              <input
                type={showPassword ? "text" : "password"}
                id="password"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                placeholder="Enter your password"
                required
              />
              <button
                type="button"
                className="password-toggle"
                onClick={() => setShowPassword(!showPassword)}
                tabIndex={-1}
              >
                <FontAwesomeIcon icon={showPassword ? faEyeSlash : faEye} />
              </button>
            </div>
          </div>

          {companies.length > 0 && (
            <div className="form-group">
              <label htmlFor="company">Company</label>
              <select
                id="company"
                value={selectedCompanyId}
                onChange={(e) => setSelectedCompanyId(e.target.value)}
                required
              >
                <option value="">Select company</option>
                {companies.map((company) => (
                  <option
                    key={company.id || company._id}
                    value={company.id || company._id}
                  >
                    {company.name}
                  </option>
                ))}
              </select>
            </div>
          )}

          <button type="submit" className="login-button" disabled={loading}>
            {loading ? "Signing In..." : "Sign In"}
          </button>
        </form>

        <div className="login-footer">
          <button
            type="button"
            className="forgot-password"
            onClick={handleForgotPasswordOpen}
          >
            Forgot Password?
          </button>
        </div>
      </div>
      <div className="login-copyright">
        USPTO Patent is Pending | CopyRight @2025-2026
      </div>
      {showForgotPassword && (
        <div className="modal-overlay" onClick={handleForgotPasswordClose}>
          <div
            className="modal-content"
            style={{ maxWidth: 440 }}
            onClick={(e) => e.stopPropagation()}
          >
            {forgotPasswordSuccess ? (
              <>
                <h3 style={{ marginTop: 0 }}>Check Your Email</h3>
                <p style={{ color: "#475569", lineHeight: 1.6 }}>
                  If an account exists with that email, we&apos;ve sent a temporary
                  password and a link to update it. Please check your inbox and
                  spam folder.
                </p>
                <ol style={{ color: "#475569", margin: "16px 0", paddingLeft: 20, lineHeight: 1.8 }}>
                  <li>Open the email and use the temporary password</li>
                  <li>Sign in with your email and temporary password</li>
                  <li>You&apos;ll be prompted to set a new password on the Update Password page</li>
                </ol>
                <button
                  type="button"
                  className="forgot-modal-submit"
                  onClick={handleForgotPasswordClose}
                  style={{ marginTop: 8 }}
                >
                  Back to Login
                </button>
              </>
            ) : (
              <>
                <h3 style={{ marginTop: 0 }}>Forgot Password?</h3>
                <p style={{ color: "#475569", marginBottom: 16 }}>
                  Enter your email address and we&apos;ll send you a temporary
                  password and a link to update it.
                </p>
                <form onSubmit={handleForgotPasswordSubmit}>
                  <div className="form-group">
                    <label htmlFor="forgot-email">Email Address</label>
                    <input
                      type="email"
                      id="forgot-email"
                      value={forgotPasswordEmail}
                      onChange={(e) => {
                        setForgotPasswordEmail(e.target.value);
                        setForgotPasswordError("");
                      }}
                      placeholder="Enter your email"
                      autoFocus
                    />
                  </div>
                  {forgotPasswordError && (
                    <div className="login-error" style={{ marginBottom: 12 }}>
                      {forgotPasswordError}
                    </div>
                  )}
                  <div className="forgot-modal-actions">
                    <button
                      type="button"
                      className="forgot-modal-cancel"
                      onClick={handleForgotPasswordClose}
                    >
                      Cancel
                    </button>
                    <button
                      type="submit"
                      className="forgot-modal-submit"
                      disabled={forgotPasswordLoading}
                    >
                      {forgotPasswordLoading ? "Sending..." : "Send Temporary Password"}
                    </button>
                  </div>
                </form>
              </>
            )}
          </div>
        </div>
      )}
      {termsRequired && (
        <div className="modal-overlay">
          <div className="modal-content" style={{ maxWidth: 520 }}>
            <h3 style={{ marginTop: 0 }}>Terms of Use</h3>
            <p style={{ color: "#475569" }}>
              Please review and accept the Terms of Use to continue.
            </p>
            <label
              style={{
                display: "flex",
                gap: "10px",
                alignItems: "flex-start",
                marginTop: "16px",
              }}
            >
              <input
                type="checkbox"
                checked={termsChecked}
                onChange={(e) => setTermsChecked(e.target.checked)}
              />
              <span>
                I have read, fully understand, and accept the Terms of Use for
                ALBERTai, a Unity Global Care company. Skip this prompt on
                future logins unless the Terms of Use change.
              </span>
            </label>
            <div style={{ marginTop: "12px" }}>
              <a
                href="/admin/terms-of-use"
                target="_blank"
                rel="noopener noreferrer"
              >
                View Terms of Use
              </a>
            </div>
            {termsError && (
              <div className="login-error" style={{ marginTop: "12px" }}>
                {termsError}
              </div>
            )}
            <div
              className="modal-actions"
              style={{ justifyContent: "flex-end", marginTop: "20px" }}
            >
              <button
                className="create-button active"
                onClick={handleAcceptTerms}
                disabled={termsSaving}
              >
                {termsSaving ? "Saving..." : "Accept & Continue"}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default Login;
